import { useState } from 'react';
import { Link } from 'react-router';
import { api, type FeatureControl, type FeatureView } from '../../api/client';
import { Button, Card, Chip, Dialog, LoadError, Loading, Notice } from '../../components/ui';
import { shortWhen, type Tone } from '../../lib/format';
import { useData } from '../../lib/useData';
import { usePoll } from './Updates';

/**
 * Every feature this customer has, in one list (1.13.0, Teampace 2999): on, off or blocked, why,
 * who turns it on, and the switch (Stitch's own), a button (Make them), or a link to where it's
 * changed (the company's Settings, System, the Updates card below). The latest changes under each.
 */
export const FEATURE_STATE: Record<FeatureView['state'], { label: string; tone: Tone }> = {
  on: { label: 'On', tone: 'ok' },
  off: { label: 'Off', tone: 'neutral' },
  blocked: { label: 'Blocked', tone: 'warn' }
};

/** Turning these off cuts something people are using: asked first. */
const ASK_BEFORE_OFF = new Set(['app-calling', 'texting', 'voicemail-emails']);

export function FeaturesCard({ customerId, name }: { customerId: string; name: string }) {
  const data = useData(() => api.customerFeatures(customerId), [customerId]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState<FeatureView | null>(null);
  const list = data.data?.features ?? [];
  usePoll(list.some((f) => f.running), data.reload);
  const open = (data.data?.requests ?? []).filter((r) => r.status === 'open');

  const set = async (f: FeatureView, on: boolean) => {
    setBusy(f.id);
    setError('');
    try {
      await api.setCustomerFeature(customerId, f.id, on);
      data.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
      setConfirm(null);
    }
  };

  const control = (f: FeatureView, c: FeatureControl | null | undefined) => {
    if (!c) return null;
    if (c.type === 'link') {
      return c.to.startsWith('#') ? (
        <a className="btn small ghost" href={c.to}>
          {c.label}
        </a>
      ) : (
        <Link className="btn small ghost" to={c.to}>
          {c.label}
        </Link>
      );
    }
    const going = busy === f.id || Boolean(f.running);
    if (c.type === 'button') {
      return (
        <Button size="small" variant="primary" disabled={going} onClick={() => void set(f, true)}>
          {going ? 'Working…' : c.label}
        </Button>
      );
    }
    return c.on ? (
      <Button size="small" variant="ghost" disabled={going} onClick={() => (ASK_BEFORE_OFF.has(f.id) ? setConfirm(f) : void set(f, false))}>
        {going ? 'Working…' : 'Turn off'}
      </Button>
    ) : (
      <Button size="small" variant="primary" disabled={going} onClick={() => void set(f, true)}>
        {going ? 'Working…' : 'Turn on'}
      </Button>
    );
  };

  const counts = { on: list.filter((f) => f.state === 'on').length, off: list.filter((f) => f.state === 'off').length, blocked: list.filter((f) => f.state === 'blocked').length };
  return (
    <Card
      title="Features"
      id="features"
      action={list.length ? <span className="muted">{`${counts.on} on · ${counts.off} off · ${counts.blocked} blocked`}</span> : undefined}
    >
      {data.loading && !data.data ? (
        <Loading />
      ) : data.error ? (
        <LoadError error={data.error} retry={data.reload} />
      ) : (
        <>
          {error && <Notice tone="crit">{error}</Notice>}
          {open.length > 0 && (
            <Notice tone="warn" icon="bell">
              {name} asked Stitch to turn on {open.map((r) => r.featureName).join(', ')}. Turning it on here answers the request, and whoever asked is told.{' '}
              <Link to="/admin/requests">Requests</Link>
            </Notice>
          )}
          <ul className="features" aria-label="Features">
            {list.map((f) => {
              const asked = open.find((r) => r.feature === f.id);
              return (
                <li key={f.id} id={`feature-${f.id}`} className={`feature ${f.state}`}>
                  <div className="feature-main">
                    <div className="feature-title">
                      <strong>{f.name}</strong>
                      <Chip small tone={FEATURE_STATE[f.state].tone}>
                        {FEATURE_STATE[f.state].label}
                      </Chip>
                      {asked && (
                        <Chip small tone="brand">
                          Asked for
                        </Chip>
                      )}
                      {f.running && <Chip small>Working…</Chip>}
                    </div>
                    <span className="muted">{f.what}</span>
                    {f.reason && <span className="feature-reason">{f.reason}</span>}
                    {f.detail && <span className="muted">{f.detail}</span>}
                    <span className="feature-owner muted">{f.ownerWords}</span>
                    {f.history && f.history.length > 0 && (
                      <ul className="feature-history" aria-label={`${f.name}: recent changes`}>
                        {f.history.map((h) => (
                          <li key={`${h.at}${h.what}`} className="muted">
                            {shortWhen(h.at)} · {h.by}: {h.what}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="feature-control">{control(f, f.control)}</div>
                </li>
              );
            })}
          </ul>
        </>
      )}
      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm ? `Turn off ${confirm.name}?` : ''}
        subtitle={confirm ? `For ${name}. People there lose it at once; it’s recorded in Stitch’s log and their Activity.` : ''}
        foot={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Keep it on
            </Button>
            <Button variant="danger" disabled={busy !== ''} onClick={() => confirm && void set(confirm, false)}>
              Turn it off
            </Button>
          </>
        }
      >
        <p className="muted">{confirm?.what}</p>
      </Dialog>
    </Card>
  );
}
